import React, { useEffect, useState } from 'react';
import './PaymentPage.css';
import { useTranslation } from 'react-i18next';
import {useNavigate} from "react-router-dom";

export default function PaymentPage({ userData, onOrderComplete }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language === 'ua' ? 'ua' : 'en';
    const navigate = useNavigate();

    // Кошик
    const [cartItems, setCartItems] = useState([]);
    const [uniqueItemsCount, setUniqueItemsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Дані форми
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [isUkraine, setIsUkraine] = useState(lang === 'ua');
    const [shippingMethod, setShippingMethod] = useState('nova'); // "nova" / "ukrposhta"
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [branch, setBranch] = useState(''); // відділення / поштовий індекс / адреса

    // Стани для модалки успішного замовлення
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderId, setOrderId] = useState(null);

    // Маркетингові тексти
    const labelCartTitleUa = `Ваш кошик — крок до нових вражень!`;
    const labelCartTitleEn = `Your Cart — one step away from your new experience!`;
    const labelShippingUa = "Швидка доставка вашого успіху";
    const labelShippingEn = "Fast shipping for your success";

    // Lambda endpoint
    const LAMBDA_URL = 'https://thskp6o2fa52da7h27qkh6ndw40hfban.lambda-url.eu-west-2.on.aws';

    useEffect(() => {
        loadCart().then(r => console.log('Cart loaded'));
    }, []);

    // useEffect(() => {
    //     // Якщо не loading, і кошик порожній => редіректимо
    //     if (!isLoading && cartItems.length === 0) {
    //
    //     }
    // }, [isLoading, cartItems, navigate]);

    // ========================
    // Зчитування кошика
    // ========================
    const loadCart = async () => {
        setIsLoading(true);
        setCartItems([]);
        setUniqueItemsCount(0);

        if (userData?.token) {
            try {
                const res = await fetch(`${LAMBDA_URL}/cart`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userData.token}`
                    }
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || 'Error fetching cart');

                const items = data.items || [];
                setCartItems(items);
                setUniqueItemsCount(data.uniqueItemsCount || items.length);
                if (items.length === 0) navigate('/');
            } catch (err) {
                navigate('/');
                console.error('Could not load cart:', err);
            } finally {
                setIsLoading(false);
            }
        } else {
            // Якщо не авторизований => порожньо
            setIsLoading(false);
            setCartItems([]);
            setUniqueItemsCount(0);
        }
    };

    // ========================
    // Сума
    // ========================
    const calcTotal = () => {
        let sum = 0;
        cartItems.forEach(item => {
            const priceVal = Number(item?.chosenPrice?.[lang] || 0);
            sum += priceVal * (item.count || 1);
        });
        return sum.toFixed(2);
    };

    // ========================
    // Кнопка "Оплатити" => валідація + створення замовлення
    // ========================
    const handlePayment = async (e) => {
        e.preventDefault();

        // 1) Валідація: які поля потрібні?
        if (!fullName.trim()) {
            alert(lang === 'ua' ? 'Вкажіть ПІБ' : 'Please enter Full Name');
            return;
        }
        if (!phone.trim()) {
            alert(lang === 'ua' ? 'Вкажіть номер телефону' : 'Please enter phone number');
            return;
        }
        // Якщо не авторизований => потрібен email
        if (!userData?.token && !email.trim()) {
            alert(lang === 'ua' ? 'Вкажіть email' : 'Please enter email');
            return;
        }
        if (!city.trim()) {
            alert(lang === 'ua' ? 'Вкажіть місто' : 'Please enter city');
            return;
        }
        if (!branch.trim() && !isUkraine) {
            alert(lang === 'ua' ? 'Вкажіть адресу' : 'Please enter address');
            return;
        }
        if (isUkraine && !branch.trim()) {
            // якщо Nova / Ukrposhta => branch
            alert(lang === 'ua' ? 'Вкажіть відділення або індекс' : 'Please enter branch / index');
            return;
        }

        // 2) Формуємо orderData
        const orderData = {
            fullName,
            phone,
            email: userData?.token ? userData?.email || '' : email,
            isUkraine,
            shippingMethod: isUkraine ? shippingMethod : 'international',
            city,
            branch,
            country: isUkraine ? 'Ukraine' : country,  // якщо isUkraine => Ukraine, інакше користувач введе
            cartItems,  // якщо userData?.token => лямбда сама візьме з кошика, але дублюємо
        };

        // 3) Надсилаємо на /create-order
        try {
            const res = await fetch(`${LAMBDA_URL}/create-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(userData?.token && { 'Authorization': `Bearer ${userData.token}`})
                },
                body: JSON.stringify(orderData)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Create order failed');

            console.log('Order created:', data);

            console.log('Order created:', data);
            // Відкриваємо модалку з номером
            setOrderId(data.OrderId);
            setShowOrderModal(true);

            if (onOrderComplete) onOrderComplete();
        } catch (err) {
            console.error('Create order error:', err);
            setShowOrderModal(true);
            // alert('Failed to create order: ' + err.message);
        }
    };

    // Закриття модалки => / (на головну)
    const handleModalClose = () => {
        setShowOrderModal(false);
        navigate('/');
    };

    // ========================
    // Рендер товарів у кошику
    // ========================
    const renderCartItems = () => {
        return cartItems.map((item, idx) => {
            const productName = item?.title?.[lang] || 'Untitled';
            const priceVal = Number(item?.chosenPrice?.[lang] || 0);
            const totalForItem = (priceVal * (item.count || 1)).toFixed(2);

            let paramsString = '';
            if (item.chosenParamsLabels) {
                const arr = Object.values(item.chosenParamsLabels).map(obj => {
                    return obj.labels?.[lang] || obj.value;
                });
                paramsString = arr.join(', ');
            }

            const mainImgUrl = item.mainImage
                ? item.mainImage
                : '/fallback.png';

            return (
                <div className="checkout-cart-item" key={idx}>
                    <img src={mainImgUrl} alt={productName} className="checkout-cart-image" />
                    <div className="checkout-cart-info">
                        <h4>{productName}</h4>
                        {paramsString && <p className="checkout-cart-params">{paramsString}</p>}
                        <p className="checkout-cart-line">
                            {lang === 'ua' ? 'К-сть' : 'Qty'}: {item.count}{' × '}
                            <span className="checkout-cart-price">
                {lang === 'ua' ? `${priceVal} грн` : `$${priceVal}`}
              </span>
                            {' = '}
                            <span className="checkout-cart-price">
                {lang === 'ua' ? `${totalForItem} грн` : `$${totalForItem}`}
              </span>
                        </p>
                    </div>
                </div>
            );
        });
    };

    // Міжнародна доставка (якщо чекбокс знято)
    const isInternational = !isUkraine;

    return (
        <div className="payment-page-container">
            {/* ЛІВА КОЛОНКА */}
            <div className="payment-left">
                <h2>
                    {lang === 'ua' ? labelCartTitleUa : labelCartTitleEn}
                </h2>
                {isLoading ? (
                    <p className="left-loading">{lang === 'ua' ? 'Завантаження...' : 'Loading...'}</p>
                ) : cartItems.length === 0 ? (
                    <p className="left-empty">{lang === 'ua' ? 'Кошик порожній' : 'Cart is empty'}</p>
                ) : (
                    <>
                        <div className="checkout-cart-list">
                            {renderCartItems()}
                        </div>
                        <div className="checkout-cart-total">
                            {lang === 'ua' ? 'Сума:' : 'Total:'}{' '}
                            <span className="checkout-cart-sum">
                {lang === 'ua' ? `${calcTotal()} грн` : `$${calcTotal()}`}
              </span>
                        </div>
                    </>
                )}
            </div>

            {/* ПРАВА КОЛОНКА */}
            <div className="payment-right">
                <h2>
                    {lang === 'ua' ? labelShippingUa : labelShippingEn}
                </h2>
                <form className="checkout-form" onSubmit={handlePayment}>
                    <label>{lang === 'ua' ? 'Прізвище та ім’я' : 'Full Name'}</label>
                    <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder={lang === 'ua' ? 'Введіть ПІБ' : 'Enter Full Name'}
                    />

                    <label>{lang === 'ua' ? 'Номер телефону' : 'Phone Number'}</label>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={lang === 'ua' ? '380XXXXXXXXX' : '123-456-7890'}
                    />

                    {!userData?.token && (
                        <>
                            <label>{lang === 'ua' ? 'Електронна пошта' : 'Email'}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={lang === 'ua' ? 'Введіть email' : 'Enter your email'}
                            />
                        </>
                    )}

                    {/* Чекбокс: Доставка в Україні */}
                    <div className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={isUkraine}
                            onChange={(e) => setIsUkraine(e.target.checked)}
                            id="in-ukraine-check"
                        />
                        <label htmlFor="in-ukraine-check">
                            {lang === 'ua' ? 'Доставити у межах України' : 'Deliver within Ukraine'}
                        </label>
                    </div>

                    {/* Якщо isUkraine => показуємо радіо-кнопки */}
                    {!isInternational && (
                        <div className="shipping-methods-horizontal">
                            <div
                                className={`shipping-option ${shippingMethod === 'nova' ? 'shipping-active' : ''}`}
                                onClick={() => setShippingMethod('nova')}
                            >
                                <input
                                    type="radio"
                                    name="shippingMethod"
                                    value="nova"
                                    checked={shippingMethod === 'nova'}
                                    readOnly
                                />
                                <img
                                    src="/images/nova_post.png"
                                    alt="Nova Post"
                                    className="shipping-logo"
                                />
                                <span>{lang === 'ua' ? 'Нова пошта' : 'Nova Post'}</span>
                            </div>

                            <div
                                className={`shipping-option ${shippingMethod === 'ukrposhta' ? 'shipping-active' : ''}`}
                                onClick={() => setShippingMethod('ukrposhta')}
                            >
                                <input
                                    type="radio"
                                    name="shippingMethod"
                                    value="ukrposhta"
                                    checked={shippingMethod === 'ukrposhta'}
                                    readOnly
                                />
                                <img
                                    src="/images/urk_post.png"
                                    alt="Ukrposhta"
                                    className="shipping-logo"
                                />
                                <span>{lang === 'ua' ? 'Укрпошта' : 'UkrPoshta'}</span>
                            </div>
                        </div>
                    )}

                    {/* Поля залежно від методу / країни */}
                    {!isInternational && (
                        <>
                            <label>{lang === 'ua' ? 'Місто' : 'City'}</label>
                            <input
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder={lang === 'ua' ? 'Київ' : 'Kyiv'}
                            />

                            {shippingMethod === 'nova' ? (
                                <>
                                    <label>{lang === 'ua' ? 'Відділення' : 'Branch'}</label>
                                    <input
                                        type="text"
                                        value={branch}
                                        onChange={(e) => setBranch(e.target.value)}
                                        placeholder={lang === 'ua' ? 'Відділення №...' : 'Branch #...'}
                                    />
                                </>
                            ) : (
                                <>
                                    <label>{lang === 'ua' ? 'Відділення / Поштовий індекс' : 'Branch / Postal code'}</label>
                                    <input
                                        type="text"
                                        value={branch}
                                        onChange={(e) => setBranch(e.target.value)}
                                        placeholder={lang === 'ua' ? '№... / індекс' : '#... / ZIP'}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {isInternational && (
                        <>
                            <label>{lang === 'ua' ? 'Країна' : 'Country'}</label>
                            <input
                                type="text"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                placeholder={lang === 'ua' ? 'Напр. Польща' : 'e.g. Poland'}
                            />

                            <label>{lang === 'ua' ? 'Місто' : 'City'}</label>
                            <input
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder={lang === 'ua' ? 'Введіть місто' : 'Enter city'}
                            />

                            <label>{lang === 'ua' ? 'Адреса' : 'Address'}</label>
                            <input
                                type="text"
                                value={branch}
                                onChange={(e) => setBranch(e.target.value)}
                                placeholder={lang === 'ua' ? 'Вулиця...' : 'Street...'}
                            />
                        </>
                    )}

                    <button type="submit" className="checkout-pay-btn">
                        {lang === 'ua' ? 'ОПЛАТИТИ' : 'Pay Now'}
                    </button>
                </form>
            </div>

            {showOrderModal && (
                <div className="order-modal-backdrop" onClick={handleModalClose}>
                    <div className="order-modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>
                            {lang === 'ua' ? 'Замовлення оформлено!' : 'Order Created!'}
                        </h2>
                        <p>
                            {lang === 'ua'
                                ? `Ваш номер замовлення: #${orderId}`
                                : `Your order number is #${orderId}`}
                        </p>
                        <button onClick={handleModalClose} className="order-modal-button">
                            {lang === 'ua' ? 'Повернутись на сайт' : 'Return to site'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
